/**
 * Curated highlight.js language registration.
 *
 * The full `highlight.js` package bundles ~190 language grammars (~1MB minified).
 * Leedoc only needs the languages that realistically appear in server-install /
 * maintenance documentation, so we register a hand-picked subset against the
 * lightweight `highlight.js/lib/core` build. This keeps the app bundle small
 * without losing highlighting for anything authors actually write.
 *
 * To add a language: import it from `highlight.js/lib/languages/<name>` and add
 * a `hljs.registerLanguage('<name>', <name>)` line below. Aliases (e.g. `sh`,
 * `yml`, `ps1`) are provided by the grammar files themselves.
 */
import type { HLJSApi } from 'highlight.js';

import bash from 'highlight.js/lib/languages/bash';
import shell from 'highlight.js/lib/languages/shell';
import powershell from 'highlight.js/lib/languages/powershell';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import yaml from 'highlight.js/lib/languages/yaml';
import json from 'highlight.js/lib/languages/json';
import ini from 'highlight.js/lib/languages/ini';
import properties from 'highlight.js/lib/languages/properties';
import nginx from 'highlight.js/lib/languages/nginx';
import apache from 'highlight.js/lib/languages/apache';
import sql from 'highlight.js/lib/languages/sql';
import python from 'highlight.js/lib/languages/python';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import php from 'highlight.js/lib/languages/php';
import go from 'highlight.js/lib/languages/go';
import rust from 'highlight.js/lib/languages/rust';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import java from 'highlight.js/lib/languages/java';
import ruby from 'highlight.js/lib/languages/ruby';
import perl from 'highlight.js/lib/languages/perl';
import lua from 'highlight.js/lib/languages/lua';
import makefile from 'highlight.js/lib/languages/makefile';
import cmake from 'highlight.js/lib/languages/cmake';
import diff from 'highlight.js/lib/languages/diff';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import scss from 'highlight.js/lib/languages/scss';
import less from 'highlight.js/lib/languages/less';
import markdown from 'highlight.js/lib/languages/markdown';
import http from 'highlight.js/lib/languages/http';
import plaintext from 'highlight.js/lib/languages/plaintext';

export function registerHighlightLanguages(hljs: HLJSApi): void {
  hljs.registerLanguage('bash', bash);
  hljs.registerLanguage('shell', shell);
  hljs.registerLanguage('powershell', powershell);
  hljs.registerLanguage('dockerfile', dockerfile);
  hljs.registerLanguage('yaml', yaml);
  hljs.registerLanguage('json', json);
  hljs.registerLanguage('ini', ini);
  hljs.registerLanguage('properties', properties);
  hljs.registerLanguage('nginx', nginx);
  hljs.registerLanguage('apache', apache);
  hljs.registerLanguage('sql', sql);
  hljs.registerLanguage('python', python);
  hljs.registerLanguage('javascript', javascript);
  hljs.registerLanguage('typescript', typescript);
  hljs.registerLanguage('php', php);
  hljs.registerLanguage('go', go);
  hljs.registerLanguage('rust', rust);
  hljs.registerLanguage('c', c);
  hljs.registerLanguage('cpp', cpp);
  hljs.registerLanguage('csharp', csharp);
  hljs.registerLanguage('java', java);
  hljs.registerLanguage('ruby', ruby);
  hljs.registerLanguage('perl', perl);
  hljs.registerLanguage('lua', lua);
  hljs.registerLanguage('makefile', makefile);
  hljs.registerLanguage('cmake', cmake);
  hljs.registerLanguage('diff', diff);
  hljs.registerLanguage('xml', xml);
  hljs.registerLanguage('css', css);
  hljs.registerLanguage('scss', scss);
  hljs.registerLanguage('less', less);
  hljs.registerLanguage('markdown', markdown);
  hljs.registerLanguage('http', http);
  hljs.registerLanguage('plaintext', plaintext);
}
